import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { companies, portraitImage, stats } from '../mock';

const beliefs = [
  { t: 'Judgment over frameworks', d: 'Templates are everywhere. The scarce skill is knowing which move matters now—and having the conviction to make it.' },
  { t: 'Craft plus influence', d: 'Great products are built by people who can do the work and bring others along. We train both.' },
  { t: 'Real decisions, real momentum', d: 'Every session ends with language, tools, and a next move you can use on Monday morning.' },
];

export default function About() {
  const ref = useReveal();
  return (
    <div ref={ref} className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
      <section className="grid md:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
        <div className="reveal in">
          <span className="eyebrow text-[#2E6BFF]">Meet your guide</span>
          <h1 className="font-display font-extrabold text-[#0E1726] text-[40px] sm:text-[52px] tracking-tight leading-[1.02] mt-4">
            Rajni Sharma has built products—and the organizations behind them.
          </h1>
          <p className="text-[#5B6472] text-[17px] leading-relaxed mt-6">
            Rajni is a product executive, founder, educator, and Forbes Technology Council member. Across
            Amazon, Apple, Microsoft, eBay, Rubrik, Paylocity, and Walmart, she has led platforms, marketplaces,
            identity systems, and enterprise products used at significant scale.
          </p>
          <p className="text-[#5B6472] text-[17px] leading-relaxed mt-4">
            Her teaching connects product craft with the harder skills that change careers: judgment, influence,
            storytelling, resilience, and knowing what deserves your energy.
          </p>
        </div>
        <div className="reveal in">
          <div className="rounded-[24px] overflow-hidden shadow-[0_30px_80px_-40px_rgba(14,23,38,0.4)]">
            <img src={portraitImage} alt="Rajni Sharma" loading="eager" decoding="async" className="w-full h-[440px] object-cover object-top" />
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="reveal bg-white rounded-2xl p-6 shadow-[0_20px_50px_-38px_rgba(14,23,38,0.4)]">
              <div className="font-display font-extrabold text-[#0E1726] text-[24px]">{s.value}</div>
              <div className="text-[#5B6472] text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-6">
        <h2 className="reveal font-display font-extrabold text-[#0E1726] text-[30px] sm:text-[38px] tracking-tight max-w-xl">
          What Rajni believes about growth.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {beliefs.map((b, i) => (
            <div key={b.t} className="reveal bg-white rounded-2xl p-7 border-t-[3px] border-[#2E6BFF]">
              <div className="font-display font-extrabold text-[#2E6BFF] text-[30px]">0{i + 1}</div>
              <h3 className="font-display font-bold text-[#0E1726] text-[19px] mt-3">{b.t}</h3>
              <p className="text-[#5B6472] leading-relaxed mt-2">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14">
        <span className="eyebrow text-[#5B6472]">Where she has built</span>
        <div className="flex flex-wrap gap-2.5 mt-4">
          {companies.map((c) => (
            <span key={c} className="bg-white border border-black/10 rounded-full px-5 py-2.5 font-semibold text-[#0E1726]">{c}</span>
          ))}
        </div>
      </section>

      <section className="py-4">
        <div className="reveal bg-[#2E6BFF] rounded-[28px] p-10 sm:p-14 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div>
            <h2 className="font-display font-extrabold text-[30px] sm:text-[38px] tracking-tight">Ready to build your next chapter?</h2>
            <p className="text-white/85 mt-3 max-w-md">Tell Rajni what you're trying to change and find the right place to begin.</p>
          </div>
          <Link to="/contact" className="bg-white text-[#0E1726] font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 hover:bg-white/90 transition-colors whitespace-nowrap">
            Talk to Rajni <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
